// client/src/components/AdminSuperadmins.js
// Superadmin management page for superadmins

import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { HeadlineMedium, LabelMedium, BodyMedium } from './Typography';
import { db } from '../firebase';
import { collection, getDocs, query, where, doc, updateDoc, getDoc } from 'firebase/firestore';
import { isSuperAdmin } from '../utils/roleUtils';
import Toast from './Toast';

const AdminSuperadmins = () => {
  const { userRole } = useAuth();
  const [superadmins, setSuperadmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [addingSuperadmin, setAddingSuperadmin] = useState(false);
  const [removingSuperadmin, setRemovingSuperadmin] = useState({});
  
  // Toast notification state
  const [toastVisible, setToastVisible] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');

  // Toast helper functions
  const showToast = (message, type = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setToastVisible(true);
  };

  const hideToast = () => {
    setToastVisible(false);
  };

  // Load all superadmins
  const loadSuperadmins = async () => {
    try {
      setLoading(true);
      const usersSnapshot = await getDocs(
        query(
          collection(db, 'users'),
          where('role', '==', 'superadmin')
        )
      );
      
      const superadminList = [];
      usersSnapshot.forEach((doc) => {
        superadminList.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      // Sort by display name or email
      superadminList.sort((a, b) => {
        const nameA = a.display_name || a.email || '';
        const nameB = b.display_name || b.email || '';
        return nameA.localeCompare(nameB);
      });
      
      setSuperadmins(superadminList);
    } catch (error) {
      console.error('Error loading superadmins:', error);
      showToast('Failed to load superadmins', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isSuperAdmin(userRole)) {
      loadSuperadmins();
    }
  }, [userRole]);

  // Handle adding a superadmin
  const handleAddSuperadmin = async (e) => {
    e.preventDefault();
    
    if (!emailInput.trim()) {
      showToast('Please enter an email address', 'error');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailInput.trim())) {
      showToast('Please enter a valid email address', 'error');
      return;
    }

    setAddingSuperadmin(true);

    try {
      const email = emailInput.trim().toLowerCase();
      
      // Find user by email
      const usersSnapshot = await getDocs(
        query(
          collection(db, 'users'),
          where('email', '==', email)
        )
      );

      if (usersSnapshot.empty) {
        showToast('User not found. Please ensure the user has signed up first.', 'error');
        return;
      }

      const userDoc = usersSnapshot.docs[0];
      const userData = userDoc.data();

      // Check if already a superadmin
      if (userData.role === 'superadmin') {
        showToast('User is already a superadmin', 'error');
        return;
      }

      // Update user role to superadmin
      await updateDoc(doc(db, 'users', userDoc.id), {
        role: 'superadmin',
        updatedAt: new Date()
      });

      showToast(`Successfully added ${email} as superadmin`, 'success');
      setEmailInput('');
      setShowAddModal(false);
      
      // Reload superadmins list
      await loadSuperadmins();
    } catch (error) {
      console.error('Error adding superadmin:', error);
      showToast('Failed to add superadmin. Please try again.', 'error');
    } finally {
      setAddingSuperadmin(false);
    }
  };

  // Handle removing a superadmin
  const handleRemoveSuperadmin = async (superadminId, email) => {
    if (!window.confirm(`Are you sure you want to remove superadmin privileges from ${email}?`)) {
      return;
    }

    setRemovingSuperadmin(prev => ({ ...prev, [superadminId]: true }));

    try {
      // Update user role to admin (default back to admin)
      await updateDoc(doc(db, 'users', superadminId), {
        role: 'admin',
        updatedAt: new Date()
      });

      showToast(`Successfully removed superadmin privileges from ${email}`, 'success');
      
      // Reload superadmins list
      await loadSuperadmins();
    } catch (error) {
      console.error('Error removing superadmin:', error);
      showToast('Failed to remove superadmin. Please try again.', 'error');
    } finally {
      setRemovingSuperadmin(prev => ({ ...prev, [superadminId]: false }));
    }
  };

  // Only superadmins can access this page
  if (!isSuperAdmin(userRole)) {
    return (
      <div className="text-center py-12">
        <BodyMedium color="red-600">Access denied. Superadmin privileges required.</BodyMedium>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading superadmins...</span>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Toast Notification */}
      <Toast
        message={toastMessage}
        type={toastType}
        isVisible={toastVisible}
        onClose={hideToast}
        duration={3000}
      />

      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-6">
          <HeadlineMedium color="gray-900">Superadmins</HeadlineMedium>
          <button
            onClick={() => setShowAddModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <LabelMedium color="white">Add Superadmin</LabelMedium>
          </button>
        </div>

        <BodyMedium color="gray-600" className="mb-6">
          Manage users with superadmin privileges. Superadmins have full access to all organizations and system settings.
        </BodyMedium>

        {/* Superadmins Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4">
                  <LabelMedium color="gray-600">Name</LabelMedium>
                </th>
                <th className="text-left py-3 px-4">
                  <LabelMedium color="gray-600">Email</LabelMedium>
                </th>
                <th className="text-right py-3 px-4">
                  <LabelMedium color="gray-600">Actions</LabelMedium>
                </th>
              </tr>
            </thead>
            <tbody>
              {superadmins.map((superadmin) => (
                <tr key={superadmin.id} className="border-b border-gray-100">
                  <td className="py-3 px-4">
                    <BodyMedium color="gray-900">
                      {superadmin.display_name || 'N/A'}
                    </BodyMedium>
                  </td>
                  <td className="py-3 px-4">
                    <BodyMedium color="gray-900">{superadmin.email}</BodyMedium>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <button
                      onClick={() => handleRemoveSuperadmin(superadmin.id, superadmin.email)}
                      disabled={removingSuperadmin[superadmin.id]}
                      className="p-1 text-gray-400 hover:text-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      title="Remove superadmin privileges"
                    >
                      {removingSuperadmin[superadmin.id] ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      )}
                    </button>
                  </td>
                </tr>
              ))}
              
              {superadmins.length === 0 && (
                <tr>
                  <td colSpan="3" className="py-8 text-center">
                    <BodyMedium color="gray-500">No superadmins found</BodyMedium>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Superadmin Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <HeadlineMedium color="gray-900">Add Superadmin</HeadlineMedium>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setEmailInput('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleAddSuperadmin} className="space-y-4">
              <div>
                <LabelMedium color="gray-700" className="block mb-2">Email Address</LabelMedium>
                <input
                  type="email"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter email address"
                  required
                  autoFocus
                />
                <BodyMedium color="gray-600" className="mt-2 text-sm">
                  The user must already have an account. Enter their email address to grant superadmin privileges.
                </BodyMedium>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <BodyMedium color="blue-800">
                  <strong>Note:</strong> This will grant the user full superadmin access to all organizations and system settings.
                </BodyMedium>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setEmailInput('');
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  disabled={addingSuperadmin}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={addingSuperadmin}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {addingSuperadmin ? 'Adding...' : 'Add Superadmin'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSuperadmins;
